import { FC, useState, useEffect } from 'react';
import { Modal, Stack, Group, Text, Divider, Box, Grid } from '@mantine/core';
import { Patient, RelatedPerson } from '@medplum/fhirtypes';
import {
  IconId,
  IconCake,
  IconMail,
  IconMapPin,
  IconPhone,
  IconGenderMale,
  IconGenderFemale,
  TablerIcon,
  IconGenderAgender,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { HL7, IDENTIFIERS } from '@consts/systems.consts';
import { calculateAge } from '@utils/calculate-age';
import { formatPhoneNumberIntl } from 'react-phone-number-input';
import { formatHumanName } from '@medplum/core';
import dayjs from 'dayjs';
import { useRelatedPeople } from '@/hooks/useRelatedPeople';

interface PatientViewDemographicsProps {
  opened: boolean;
  onClose: () => void;
  patient: Patient | null;
}

type ItemProps = {
  label: string;
  value: string | undefined | null;
  icon: TablerIcon;
};

const ViewItem: FC<ItemProps> = ({ label, value, icon: Icon }) => {
  const displayValue = value || '-';
  return (
    <Group gap="md" align="flex-start">
      <Icon size={18} color="var(--mantine-color-dimmed)" />
      <Box flex={1}>
        <Text size="sm" fw={500} c="dark" mb={2}>
          {label}
        </Text>
        <Text size="sm" c="dimmed">
          {displayValue}
        </Text>
      </Box>
    </Group>
  );
};

//------------------------------------------------------------------------------
interface RelatedPersonCardProps {
  relatedPerson: RelatedPerson | null;
  relationshipCode: string;
}

const RelatedPersonCard: FC<RelatedPersonCardProps> = ({
  relatedPerson,
  relationshipCode,
}) => {
  const { t } = useTranslation(['patient-pages', 'create-patient-modal']);

  const relationshipMap: Record<string, string> = {
    FTH: t('summaryPage.summary.relatedPeople.relationships.father', {
      ns: 'patient-pages',
    }),
    MTH: t('summaryPage.summary.relatedPeople.relationships.mother', {
      ns: 'patient-pages',
    }),
    SPS: t('summaryPage.summary.relatedPeople.relationships.spouse', {
      ns: 'patient-pages',
    }),
  };

  const relationshipName =
    relationshipMap[relationshipCode || ''] ||
    t('summaryPage.summary.relatedPeople.relationships.unknown', {
      ns: 'patient-pages',
    });

  const relatedPhone = relatedPerson?.telecom?.find(
    (t) => t.system === 'phone'
  )?.value;
  const relatedEmail = relatedPerson?.telecom?.find(
    (t) => t.system === 'email'
  )?.value;
  const relatedAmka = relatedPerson?.identifier?.find(
    (t) => t.system === IDENTIFIERS.AMKA
  )?.value;
  const relatedCity = relatedPerson?.address?.[0]?.city;

  return (
    <>
      <Divider label={relationshipName} />

      <Text size="sm" fw={500} mb="sm">
        {relatedPerson ? formatHumanName(relatedPerson.name?.[0]) : '-'}
      </Text>
      <Grid>
        <Grid.Col span={6}>
          <Stack gap="xs" pl="md">
            <ViewItem
              label={t('banner.amka', {
                ns: 'patient-pages',
                defaultValue: 'AMKA',
              })}
              value={relatedAmka || '-'}
              icon={IconId}
            />
            <ViewItem
              label={t('labels.phone', {
                ns: 'create-patient-modal',
                defaultValue: 'Phone',
              })}
              value={relatedPhone ? formatPhoneNumberIntl(relatedPhone) : '-'}
              icon={IconPhone}
            />
          </Stack>
        </Grid.Col>
        <Grid.Col span={6}>
          <Stack gap="xs" pl="md">
            <ViewItem
              label={t('labels.email', {
                ns: 'create-patient-modal',
                defaultValue: 'Email',
              })}
              value={relatedEmail || '-'}
              icon={IconMail}
            />
            <ViewItem
              label={t('banner.city', {
                ns: 'patient-pages',
                defaultValue: 'City',
              })}
              value={relatedCity || '-'}
              icon={IconMapPin}
            />
          </Stack>
        </Grid.Col>
      </Grid>
    </>
  );
};

//------------------------------------------------------------------------------
/**
 * View-only component for displaying patient demographics in a modal
 *
 * @param props - Component props
 * @param props.opened - Whether the modal is open
 * @param props.onClose - Callback to close the modal
 * @param props.patient - Patient to display
 * @returns JSX.Element
 */
export const PatientViewDemographics: FC<PatientViewDemographicsProps> = ({
  opened,
  onClose,
  patient,
}) => {
  const { t } = useTranslation(['patient-pages', 'create-patient-modal']);

  // Refresh key for related people - increment when modal opens to refresh data
  const [relatedPeopleRefreshKey, setRelatedPeopleRefreshKey] = useState(0);

  // Refresh related people when modal opens
  useEffect(() => {
    if (opened) {
      setRelatedPeopleRefreshKey((prev) => prev + 1);
    }
  }, [opened]);

  const relatedPeople = useRelatedPeople({
    patientId: patient?.id || '',
    refreshKey: relatedPeopleRefreshKey,
  });

  if (!patient) {
    return null;
  }

  const fullName = formatHumanName(patient.name?.[0]);
  const amka = patient?.identifier?.find(
    (id) => id.system === IDENTIFIERS.AMKA
  )?.value;
  const birthDate = patient.birthDate
    ? dayjs(patient.birthDate).format('DD/MM/YYYY')
    : null;
  const age = patient.birthDate ? calculateAge(patient.birthDate) : null;
  const city = patient?.address?.[0]?.city;
  const addressLine = patient?.address?.[0]?.line?.[0];
  const postalCode = patient?.address?.[0]?.postalCode;
  const phone = patient?.telecom?.find((t) => t.system === 'phone')?.value;
  const email = patient.telecom?.find((t) => t.system === 'email')?.value;
  const gender = patient.gender
    ? t(`summaryPage.summary.gender.${patient.gender}` as any, {
        ns: 'patient-pages',
      })
    : null;

  // Gender icon
  const GenderIcon =
    patient.gender === 'female'
      ? IconGenderFemale
      : patient.gender === 'male'
        ? IconGenderMale
        : IconGenderAgender;

  // Helper function to find related person by relationship code
  const getRelatedPersonByCode = (code: string): RelatedPerson | null => {
    return (
      relatedPeople.find(
        (person) => person.relationship?.[0]?.coding?.[0]?.code === code
      ) || null
    );
  };

  // Always show father, mother, and spouse in order
  const father = getRelatedPersonByCode('FTH');
  const mother = getRelatedPersonByCode('MTH');
  const spouse = getRelatedPersonByCode('SPS');

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Text size="xl" fw={600}>
          {fullName}
        </Text>
      }
      size="lg"
      centered
    >
      <Stack gap="md">
        <Grid>
          <Grid.Col span={6}>
            {/* Basic Information */}
            <Stack gap="sm">
              <ViewItem
                label={t('banner.amka', {
                  ns: 'patient-pages',
                  defaultValue: 'AMKA',
                })}
                value={amka}
                icon={IconId}
              />

              <ViewItem
                label={t('banner.dateOfBirth', {
                  ns: 'patient-pages',
                  defaultValue: 'Date of Birth',
                })}
                value={
                  age !== null
                    ? `${birthDate} (${age} ${t('banner.years', {
                        ns: 'patient-pages',
                        defaultValue: 'years',
                      })})`
                    : birthDate
                }
                icon={IconCake}
              />

              <ViewItem
                label={t('labels.gender', {
                  ns: 'create-patient-modal',
                  defaultValue: 'Gender',
                })}
                value={gender}
                icon={GenderIcon}
              />
            </Stack>
          </Grid.Col>
          <Grid.Col span={6}>
            {/* Contact Information */}
            <Stack gap="sm">
              <ViewItem
                label={t('labels.phone', {
                  ns: 'create-patient-modal',
                  defaultValue: 'Phone',
                })}
                value={phone ? formatPhoneNumberIntl(phone) : '-'}
                icon={IconPhone}
              />

              <ViewItem
                label={t('labels.email', {
                  ns: 'create-patient-modal',
                  defaultValue: 'Email',
                })}
                value={email}
                icon={IconMail}
              />
            </Stack>
          </Grid.Col>
        </Grid>

        <Grid>
          <Grid.Col span={6}>
            <Group gap="md" align="flex-start">
              <IconMapPin size={18} color="var(--mantine-color-dimmed)" />
              <Box flex={1}>
                <Text size="sm" fw={500} c="dark" mb={2}>
                  {t('labels.addressLine', {
                    ns: 'create-patient-modal',
                    defaultValue: 'Address Line',
                  })}
                </Text>
                <Text size="sm" c="dimmed">
                  {addressLine}
                </Text>
                <Group>
                  <Text size="sm" fw={500} c="dark" mb={2}>
                    {t('labels.postalCode', {
                      ns: 'create-patient-modal',
                      defaultValue: 'Postal Code',
                    })}
                  </Text>
                  <Text size="sm" c="dimmed">
                    {postalCode}
                  </Text>
                </Group>
              </Box>
            </Group>
          </Grid.Col>
          <Grid.Col span={6}>
            <ViewItem
              label={t('banner.city', {
                ns: 'patient-pages',
                defaultValue: 'City',
              })}
              value={city}
              icon={IconMapPin}
            />
          </Grid.Col>
        </Grid>

        {/* Related People */}
        <Stack gap="md">
          <RelatedPersonCard
            relatedPerson={father}
            relationshipCode={HL7.TERMINOLOGY.CS.V3.ROLE_CODE.CODES.FATHER}
          />
          <RelatedPersonCard
            relatedPerson={mother}
            relationshipCode={HL7.TERMINOLOGY.CS.V3.ROLE_CODE.CODES.MOTHER}
          />
          <RelatedPersonCard
            relatedPerson={spouse}
            relationshipCode={HL7.TERMINOLOGY.CS.V3.ROLE_CODE.CODES.SPOUSE}
          />
        </Stack>
      </Stack>
    </Modal>
  );
};
